import { SystemStyleObject } from '@chakra-ui/react'

import { fontFamilies } from '../../theme/fontFamilies'
import type { ColorScheme } from '../../types'

export type { ColorScheme }

export type TooltipPlacement = 'top' | 'right' | 'bottom' | 'left'

/**
 * `label` is the default — a short, single-line, uppercase hint (a name, a status, "Copy").
 * `description` is for longer explanatory copy: normal case, wraps, and gets a fixed max width
 * instead of the single-line label's `whiteSpace: nowrap`.
 */
export type TooltipVariant = 'label' | 'description'

const variantStyles: Record<TooltipVariant, SystemStyleObject> = {
  label: {
    fontSize: '10px',
    fontWeight: '500',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  description: {
    fontSize: '12px',
    fontWeight: '400',
    letterSpacing: 'normal',
    textTransform: 'none',
    whiteSpace: 'normal',
    maxW: '280px',
  },
}

const tokens = {
  dark: {
    bg: 'bg.label-tooltip',
    text: 'text.label-tooltip',
    border: 'border.label-tooltip',
  },
  light: {
    bg: 'light.bg.label-tooltip',
    text: 'light.text.label-tooltip',
    border: 'light.border.label-tooltip',
  },
}

const transforms: Record<TooltipPlacement, string> = {
  top: 'translateX(-50%) translateY(calc(-100% - 6px))',
  right: 'translateY(-50%)',
  bottom: 'translateX(-50%) translateY(6px)',
  left: 'translateX(calc(-100% - 6px)) translateY(-50%)',
}

export const tooltipStyles = (
  top: number,
  left: number,
  colorScheme: ColorScheme = 'dark',
  placement: TooltipPlacement = 'top',
  variant: TooltipVariant = 'label',
  isInteractive = false
): SystemStyleObject => ({
  position: 'fixed',
  top: `${top}px`,
  left: `${left}px`,
  transform: transforms[placement],
  bg: tokens[colorScheme].bg,
  color: tokens[colorScheme].text,
  borderWidth: '1px',
  borderColor: tokens[colorScheme].border,
  borderRadius: '4px',
  px: '10px',
  py: '5px',
  // Tooltips portal to document.body, so they cannot inherit the rebrand font
  // from the DarkProvider's DOM subtree.
  fontFamily: fontFamilies.soehne,
  // A passive hint must never swallow clicks meant for the content beneath it.
  // Only an interactive tooltip — one holding a control the operator has to
  // reach, like a copy button — opts into receiving pointer events.
  pointerEvents: isInteractive ? 'auto' : 'none',
  // Top of the dark stacking order: tooltips are hover-scoped, so they must
  // never hide behind another surface. The previous 1450 sat under Modal at
  // zIndex 1500 (the FileViewer), which swallowed the tooltips of any button
  // rendered inside it; 10000 also tops the Select menu portal (9999), the
  // package's next-highest layer.
  zIndex: 10000,
  ...variantStyles[variant],
})
